/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.controlBtn = null;
      this.muteBtn = null;
      this.onControlClick = this.togglePlayback.bind(this);
      this.onMuteClick = this.toggleMute.bind(this);
    }

    connectedCallback() {
      this.readOptions();
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    /**
     * Re-reads data attributes (e.g. after theme editor section refresh).
     */
    readOptions() {
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    /**
     * Destroys the player and control bindings so the component can be reinitialized.
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.controlBtn && this.onControlClick) {
        this.controlBtn.removeEventListener('click', this.onControlClick);
        delete this.controlBtn.dataset.bound;
      }
      this.controlBtn = null;

      if (this.muteBtn && this.onMuteClick) {
        this.muteBtn.removeEventListener('click', this.onMuteClick);
        delete this.muteBtn.dataset.bound;
      }
      this.muteBtn = null;

      if (this.player) {
        try {
          if (this.type === 'youtube' && typeof this.player.destroy === 'function') {
            this.player.destroy();
          } else if (this.type === 'vimeo' && typeof this.player.destroy === 'function') {
            this.player.destroy();
          }
        } catch (err) {
          // Player may already be destroyed when the section is removed.
        }
        this.player = null;
      }

      this.iframe = null;
      this.vimeoIsPlaying = false;
    }

    /**
     * Reinitializes the video after theme editor updates.
     */
    reinit() {
      this.destroy();
      this.readOptions();
      this.pausedByUser = false;
      this.init();
    }

    init() {
      this.initControls();

      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }
    }

    /**
     * Binds the section pause/play and mute control buttons.
     */
    initControls() {
      const section = this.closest('.video-section');
      if (!section) return;

      this.controlBtn = section.querySelector('.video-control-btn--playback');
      if (this.controlBtn && !this.controlBtn.dataset.bound) {
        this.controlBtn.dataset.bound = 'true';
        this.pauseLabel = this.controlBtn.dataset.pauseLabel
          || this.controlBtn.querySelector('.pause-icon .visually-hidden')?.textContent?.trim();
        this.playLabel = this.controlBtn.dataset.playLabel
          || this.controlBtn.querySelector('.play-icon .visually-hidden')?.textContent?.trim();

        this.controlBtn.addEventListener('click', this.onControlClick);
      }

      this.muteBtn = section.querySelector('.video-control-btn--sound');
      if (this.muteBtn && !this.muteBtn.dataset.bound) {
        this.muteBtn.dataset.bound = 'true';
        this.muteLabel = this.muteBtn.dataset.muteLabel
          || this.muteBtn.querySelector('.mute-icon .visually-hidden')?.textContent?.trim();
        this.unmuteLabel = this.muteBtn.dataset.unmuteLabel
          || this.muteBtn.querySelector('.unmute-icon .visually-hidden')?.textContent?.trim();

        this.muteBtn.addEventListener('click', this.onMuteClick);
      }
    }

    /**
     * Toggles playback when the control button is clicked.
     * @param {Event} evt - Click event.
     */
    togglePlayback(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      if (!this.player) return;

      if (this.isPlaying()) {
        this.userPause();
      } else {
        this.pausedByUser = false;
        this.play();
      }
    }

    /**
     * Pauses playback and records that the user chose to pause.
     */
    userPause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        this.player.pauseVideo();
      } else if (this.type === 'vimeo') {
        this.player.pause();
      } else {
        this.player.pause();
      }

      this.pausedByUser = true;
      this.updateControlState(true);
    }

    /**
     * Returns whether the video is currently playing.
     * @returns {boolean}
     */
    isPlaying() {
      if (!this.player) return false;

      if (this.type === 'youtube') {
        const state = this.player.getPlayerState?.();
        return state === 1 || state === 3;
      }

      if (this.type === 'vimeo') {
        return this.vimeoIsPlaying === true;
      }

      return !this.player.paused && !this.player.ended;
    }

    /**
     * Updates the pause/play control button state and labels.
     * @param {boolean} [paused] - Optional explicit paused state (for async players).
     */
    updateControlState(paused) {
      if (!this.controlBtn) return;

      const setState = (isPlaying) => {
        this.controlBtn.classList.toggle('is-paused', !isPlaying);
        this.controlBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        this.controlBtn.setAttribute(
          'aria-label',
          isPlaying ? this.pauseLabel : this.playLabel
        );
      };

      if (typeof paused === 'boolean') {
        setState(!paused);
        return;
      }

      if (this.type === 'vimeo') {
        this.player.getPaused().then((isPaused) => setState(!isPaused));
        return;
      }

      setState(this.isPlaying());
    }

    /**
     * Toggles mute when the sound control button is clicked.
     * @param {Event} evt - Click event.
     */
    toggleMute(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.isMuted()) {
          this.player.unMute();
        } else {
          this.player.mute();
        }
        this.updateMuteState();
        return;
      }

      if (this.type === 'vimeo') {
        this.player.getMuted().then((isMuted) => {
          this.player.setMuted(!isMuted);
          this.updateMuteState(!isMuted);
        });
        return;
      }

      this.player.muted = !this.player.muted;
      this.updateMuteState();
    }

    /**
     * Updates the mute control button state and labels.
     * @param {boolean} [muted] - Optional explicit muted state (for async players).
     */
    updateMuteState(muted) {
      if (!this.muteBtn) return;

      const setState = (isMuted) => {
        this.muteBtn.classList.toggle('is-muted', isMuted);
        this.muteBtn.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
        this.muteBtn.setAttribute(
          'aria-label',
          isMuted ? this.unmuteLabel : this.muteLabel
        );
      };

      if (typeof muted === 'boolean') {
        setState(muted);
        return;
      }

      if (this.type === 'youtube') {
        setState(this.player.isMuted());
        return;
      }

      if (this.type === 'vimeo') {
        this.player.getMuted().then((isMuted) => setState(isMuted));
        return;
      }

      setState(this.player.muted);
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: 0,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          controls: false,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');
        this.updateMuteState();

        this.player.on('volumechange', () => {
          this.updateMuteState();
        });

        this.player.on('pause', () => {
          this.vimeoIsPlaying = false;
          if (this.inViewport) this.pausedByUser = true;
          this.updateControlState(true);
        });

        this.player.on('play', () => {
          this.vimeoIsPlaying = true;
          this.closest('.video-section').classList.add('video-section--played');
          this.updateControlState(false);
          this.updateMuteState();
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
        this.updateControlState(true);
      });

      this.player.addEventListener('play', () => {
        this.updateControlState(false);
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      this.player.addEventListener('volumechange', () => {
        this.updateMuteState();
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.updateControlState(false);
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.updateMuteState(this.player.muted);
        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.updateMuteState(true);
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
        this.updateControlState(true);
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
        this.updateControlState(false);
        this.updateMuteState();
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }

      this.updateControlState(false);
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }

      this.updateControlState(true);
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      this.observer.observe(el);
    }
  }

  customElements.define('video-component', Video);

  /**
   * Resets and loads deferred video markup in a section (theme editor).
   * @param {HTMLElement} section - Section element from shopify:section:load.
   */
  function loadSectionVideos(section) {
    if (!section.querySelector('.video-section')) return;

    section.querySelectorAll('.video-control-btn[data-bound]').forEach((btn) => {
      delete btn.dataset.bound;
    });

    section.querySelectorAll('.video-section deferred-media').forEach((deferredMedia) => {
      if (!deferredMedia.querySelector('template video-component')) return;

      if (deferredMedia.hasAttribute('loaded')) {
        Array.from(deferredMedia.children).forEach((child) => {
          if (child.tagName !== 'TEMPLATE') child.remove();
        });
        deferredMedia.removeAttribute('loaded');
      }

      if (Shopify.designMode || !('IntersectionObserver' in window)) {
        deferredMedia.loadContent(false, false, 'editor');
      }
    });

    section.querySelectorAll('video-component').forEach((videoComponent) => {
      videoComponent.reinit();
    });
  }

  document.addEventListener('shopify:section:unload', (evt) => {
    evt.target.querySelectorAll('video-component').forEach((videoComponent) => {
      videoComponent.destroy();
    });
  });

  document.addEventListener('shopify:section:load', (evt) => {
    if (!evt.target.querySelector('.video-section')) return;

    if (Shopify.designMode) {
      setTimeout(() => loadSectionVideos(evt.target), 150);
    } else {
      loadSectionVideos(evt.target);
    }
  });
}
